// HFLedger.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './HFLedger.css';

// 모듈 임포트
import Dashboard from './HFLedger/Dashboard';
import TransactionList from './HFLedger/TransactionList';
import TransactionForm from './HFLedger/TransactionForm';
import ReportAnalytics from './HFLedger/ReportAnalytics';
import Settings from './HFLedger/Settings'; // 새로운 Settings 컴포넌트 임포트

function HFLedger({ language, user, countryCode, userIp }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('transactions');
  const [initialized, setInitialized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userSettings, setUserSettings] = useState({
    default_currency: 'KRW',
    language_preference: language || 'KR',
    theme_preference: 'light'
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);

  const SERVER_ROOT = process.env.REACT_APP_SERVER_ROOT || 'http://localhost:3001';

  // 반응형 디자인을 위한 리사이즈 이벤트 처리
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 사용자 로그인 확인 및 초기화 상태 확인
  useEffect(() => {
    if (!user) {
      // 로그인되지 않은 경우 처리
      setLoading(false);
      return;
    }

    // 가계부 초기화 여부 확인
    const checkInitialization = async () => {
      try {
        setLoading(true);
        
        // 사용자 설정 가져오기
        const settingsResponse = await axios.get(`${SERVER_ROOT}/api/hfl/settings/${user.uid}`);
        if (settingsResponse.data) {
          setUserSettings(settingsResponse.data);
          setInitialized(true);
        } else {
          // 설정이 없으면 초기화 필요
          setInitialized(false);
        }
      } catch (err) {
        console.error('Error checking initialization status:', err);
        setInitialized(false);
        // 오류가 설정을 찾을 수 없다는 것이면 초기화 필요
        if (err.response && err.response.status === 404) {
          setError(null);
        } else {
          setError(err.message || '가계부 초기화 상태 확인 중 오류가 발생했습니다.');
        }
      } finally {
        setLoading(false);
      }
    };

    checkInitialization();
  }, [user, SERVER_ROOT]);

  // 계좌 및 카테고리 정보 로드
  useEffect(() => {
    if (!user || !initialized) return;
    
    const fetchAccountsAndCategories = async () => {
      try {
        const [accountsResponse, categoriesResponse] = await Promise.all([
          axios.get(`${SERVER_ROOT}/api/hfl/accounts/${user.uid}`),
          axios.get(`${SERVER_ROOT}/api/hfl/categories/${user.uid}`)
        ]);
        
        setAccounts(accountsResponse.data);
        setCategories(categoriesResponse.data);
      } catch (err) {
        console.error('Error fetching accounts and categories:', err);
      }
    };
    
    fetchAccountsAndCategories();
  }, [user, SERVER_ROOT, initialized]);

  // 가계부 초기화
  const initializeHFLedger = async () => {
    if (!user) {
      setError('로그인이 필요합니다.');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${SERVER_ROOT}/api/hfl/initialize`, {
        user_id: user.uid,
        default_currency: userSettings.default_currency,
        language_preference: language === 'KR' ? 'ko' : 'en'
      });

      if (response.status === 201) {
        setInitialized(true);
        setUserSettings({
          ...userSettings,
          ...response.data
        });
      }
    } catch (err) {
      console.error('Error initializing HFLedger:', err);
      setError(err.message || '가계부 초기화 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 설정 변경 핸들러 
  const handleSettingsChanged = (newSettings) => {
    setUserSettings(newSettings);
  };

  // 로그인 필요 메시지
  if (!user) {
    return (
      <div className="hfledger-login-required">
        <h2>{language === 'KR' ? '로그인이 필요합니다' : 'Login Required'}</h2>
        <p>
          {language === 'KR' 
            ? '가계부 서비스를 이용하려면 로그인이 필요합니다.' 
            : 'Please login to use the financial ledger service.'}
        </p>
        <button 
          className="hfledger-button" 
          onClick={() => navigate('/login')}
        >
          {language === 'KR' ? '로그인 하기' : 'Login'}
        </button>
      </div>
    );
  }

  // 로딩 중
  if (loading) {
    return (
      <div className="hfledger-loading">
        <div className="hfledger-spinner"></div>
        <p>{language === 'KR' ? '로딩 중...' : 'Loading...'}</p>
      </div>
    );
  }

  // 초기화 필요
  if (!initialized) {
    return (
      <div className="hfledger-initialization">
        <h2>{language === 'KR' ? '가계부 초기화' : 'Initialize Financial Ledger'}</h2>
        <p>
          {language === 'KR' 
            ? '가계부 서비스를 처음 사용하시는군요! 기본 설정을 초기화하겠습니다.' 
            : "It seems this is your first time using the financial ledger service. Let's set things up!"}
        </p>
        
        <div className="hfledger-init-form">
          <div className="hfledger-form-group">
            <label htmlFor="currency">
              {language === 'KR' ? '기본 통화' : 'Default Currency'}
            </label>
            <select 
              id="currency"
              value={userSettings.default_currency}
              onChange={(e) => setUserSettings({...userSettings, default_currency: e.target.value})}
            >
              <option value="KRW">KRW (₩)</option>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="JPY">JPY (¥)</option>
              <option value="CNY">CNY (¥)</option>
              <option value="GBP">GBP (£)</option>
            </select>
          </div>
          
          {error && <div className="hfledger-error">{error}</div>}
          
          <button 
            className="hfledger-button" 
            onClick={initializeHFLedger}
          >
            {language === 'KR' ? '가계부 시작하기' : 'Start Using Financial Ledger'}
          </button>
        </div>
      </div>
    );
  }

  // 메인 컴포넌트 렌더링
  return (
    <div className="hfledger-container">
      {/* 헤더 */}
      <header className="hfledger-header">
        <h1>{language === 'KR' ? '가계부' : 'Financial Ledger'}</h1>
        {/* <div className="hfledger-currency-display">
          {userSettings.default_currency}
        </div> */}
      </header>

      {/* 네비게이션 */}
      <nav className="hfledger-nav">
        <ul className={`hfledger-tabs ${isMobile ? 'mobile' : ''}`}>
          <li 
            className={activeTab === 'transactions' ? 'active' : ''}
            onClick={() => setActiveTab('transactions')}
          >
            <i className="fas fa-plus-circle"></i>
            <span>{language === 'KR' ? '새 거래 추가' : 'Add Transaction'}</span>
          </li>
          <li 
            className={activeTab === 'dashboard' ? 'active' : ''}
            onClick={() => setActiveTab('dashboard')}
          >
            <i className="fas fa-tachometer-alt"></i>
            <span>{language === 'KR' ? '대시보드' : 'Dashboard'}</span>
          </li>
          <li 
            className={activeTab === 'transactionList' ? 'active' : ''}
            onClick={() => setActiveTab('transactionList')}
          >
            <i className="fas fa-exchange-alt"></i>
            <span>{language === 'KR' ? '거래내역' : 'Transactions'}</span>
          </li>
          <li 
            className={activeTab === 'reports' ? 'active' : ''}
            onClick={() => setActiveTab('reports')}
          >
            <i className="fas fa-chart-pie"></i>
            <span>{language === 'KR' ? '보고서' : 'Reports'}</span>
          </li>
          <li 
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            <i className="fas fa-cog"></i>
            <span>{language === 'KR' ? '설정' : 'Settings'}</span>
          </li>
        </ul>
      </nav>

      {/* 컨텐츠 영역 */}
      <main className="hfledger-content">
        {activeTab === 'dashboard' && (
          <Dashboard 
            user={user} 
            language={language} 
            SERVER_ROOT={SERVER_ROOT}
            settings={userSettings}
          />
        )}
        
        {activeTab === 'transactions' && (
          <TransactionForm 
            user={user} 
            language={language} 
            SERVER_ROOT={SERVER_ROOT}
            settings={userSettings}
            accounts={accounts}
            categories={categories}
            onSubmit={() => setActiveTab('transactionList')}
            onAccountNeeded={() => setActiveTab('settings')}
            onCategoryCreated={(newCategory) => {
              // 카테고리 목록에 새 카테고리 추가
              setCategories(prev => [...prev, newCategory]);
            }}
          />
        )}
        
        {activeTab === 'transactionList' && (
          <TransactionList 
            user={user} 
            language={language} 
            SERVER_ROOT={SERVER_ROOT}
            settings={userSettings}
          />
        )}
        
        {activeTab === 'reports' && (
          <ReportAnalytics 
            user={user} 
            language={language} 
            SERVER_ROOT={SERVER_ROOT}
            settings={userSettings}
          />
        )}
        
        {activeTab === 'settings' && (
          <Settings
            user={user}
            language={language}
            SERVER_ROOT={SERVER_ROOT}
            settings={userSettings}
            onSettingsChanged={handleSettingsChanged}
          />
        )}
      </main>

      {/* 새 거래 추가 버튼 (모바일에서는 항상 표시, 특정 탭에서만 제외) */}
      {/* {(isMobile || (activeTab !== 'transactions' && activeTab !== 'settings' && activeTab !== 'transactionList')) && (
        <div className="hfledger-add-transaction">
          <button 
            className="hfledger-floating-button"
            onClick={() => {
              // 트랜잭션 추가 탭으로 이동
              setActiveTab('transactions');
            }}
          > 
          </button>
        </div>
      )} */}
    </div>
  );
}

export default HFLedger;